<template>
  <section id="active-event" :class="{'event-not-loaded': !eventLoaded}" class="section event">
    <template v-if="eventLoaded && !isSpectatorEvent">
      <div v-show="!isModerator && activeEvent.player.name" class="welcome">Hi, <span>{{ activeEvent.player.name }}</span>!</div>
      <div v-show="isModerator" class="welcome host-warning">YOU ARE THE HOST</div>
    </template>
    <div v-if="eventLoaded && isSpectatorEvent" class="spectator-banner">
      <div @click="goToMainEvent()" v-if="isModerator" class="return-to-main"><i class="fas fa-chevron-left" /> back to main</div>
      <h2 v-else>Hi, <span>{{ activeEvent.player.name }}</span>!</h2>

      <div class="join-code">
        <template v-if="isModerator">
          <p><span>join at </span>slideswith.com</p>
          <p class="code">/{{ event.shortcode }}</p>
        </template>
        <p v-else style="letter-spacing: 0.1px">Playing as spectator</p>
      </div>

      <h2 v-if="isModerator">Spectator View</h2>
    </div>

    <SlideContainer
      v-for="(slide, key) in slideDeck.slides"
      :active="isActive(key)"
      :key="slide.id"
      :slide="slide"
      :step="step"
      :theme="slideDeck.themeData"
      :event="event"
      :secondsLeft="secondsLeft"
    />

    <div v-if="!settings.disableSounds" v-show="showPlayerControls" @click="toggleMenu('reactions')" class="reaction-fab"><span>{{ openMenu === 'reactions' ? '✖' : '🔊' }}</span></div>

    <template v-if="showPlayerControls">
      <div v-if="metaData.showPlayerBrand !== false" class="player-menu">
        <div @click="toggleMenu('account')" class="logo">
          <SlidesLogo v-if="openMenu !== 'account'" />
          <span v-else>✖</span>
        </div>
      </div>
    </template>
    <transition name="fade">
      <AccountMenu v-if="openMenu === 'account'" :eventMeta="eventMeta" />
    </transition>

    <transition name="fade">
      <PlayerModals
        v-if="showPlayerControls"
        :slides="slides"
        :currentSlide="currentSlide"
        :eventMeta="eventMeta"
      />
    </transition>

    <reactions v-if="!settings.disableSounds" :showMenu="openMenu === 'reactions'" :reactions="event.reactions" />

    <TestControls v-if="isAdminUser" :event="event" />

    <EventSubscription
      ref="eventSubscription"
      @update:eventMeta="setEventMeta"
      @update:slideDeck="setSlideDeck"
      @update:eventId="setEventId"
      @update:players="setPlayers"
      @update:responses="setResponses"
    />

    <div v-show="false" class="prefetch-images">
      <img v-for="(image, key) in imagesToPrefetch" :key="key" :src="image" class="prefetch">
    </div>
  </section>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import { saveEventSettings } from '~/queries/saveEventState.gql'
import { playerList } from '~/common/mocks/players.js'
import { animals } from '~/common/mocks/animals.js'

import EventSubscription from '~/components/EventSubscription.vue'
import SlideContainer from '~/components/SlideContainer'
import Slideshow from '~/components/mixins/Slideshow'
import TestControls from '~/components/TestControls'
import Reactions from '~/components/Reactions'
import SlidesLogo from '~/components/SlidesLogo'
import AccountMenu from '~/components/AccountMenu'
import PlayerModals from '~/components/PlayerModals'

let timeout

export default {
  head () {
    return {
      bodyAttrs: {
        class: 'event-page' + this.bodyClass,
        style: this.bodyStyle
      }
    }
  },
  auth: false,
  components: {
    SlideContainer,
    TestControls,
    Reactions,
    SlidesLogo,
    AccountMenu,
    PlayerModals,
    EventSubscription
  },
  mixins: [
    Slideshow
  ],
  props: {
    code: { default: null },
    isPreview: { default: false }
  },
  data () {
    return {
      eventId: '',
      primaryUser: '',
      slideDeck: {},
      joinedEventAt: '',
      votesAdded: false,
      wordCloudVotes: [],
      eventMeta: {
        id: '',
        shortcode: '',
        startTime: '',
        primaryUser: '',
        position: {},
        reactions: [],
        chat: {},
        settings: {}
      },
      realParticipants: [],
      realResponses: [],
      demoResponses: [],
      removedPlayers: {},
      openMenu: false,
      lastUpdate: null,
      eventLoaded: false,
      prefetchImagesToSlide: 0
    }
  },
  computed: {
    ...mapState([
      'activeEvent',
      'user'
    ]),
    ...mapGetters([
      'isAdminUser'
    ]),
    participants: {
      get () {
        if (this.eventMeta.settings.isPreviewEvent) {
          return this.demoParticipants.concat(this.realParticipants)
        } else {
          return this.realParticipants
        }
      },
      set (players) {
        this.realParticipants = players
      }
    },
    responses: {
      get () {
        if (this.eventMeta.settings.isPreviewEvent) {
          return this.realResponses.concat(this.demoResponses).concat(this.wordCloudVotes)
        } else {
          return this.realResponses
        }
      },
      set (responses) {
        this.realResponses = responses
      }
    },
    event () {
      const event = {
        ...this.eventMeta,
        participants: this.participants,
        responses: this.responses
      }

      return event
    },
    activeEvent () {
      return this.$store.state.activeEvent
    },
    demoParticipants () {
      const demoPlayerNumber = this.eventMeta.settings.testPlayers
      if (Number.isInteger(demoPlayerNumber)) {
        return playerList.slice(0, demoPlayerNumber)
      }

      return []
    },
    imagesToPrefetch () {
      const fetchFrom = Math.max(this.currentSlideIndex - 1, 0)
      return this.imagesBySlide.slice(fetchFrom, this.prefetchImagesToSlide).flat()
    },
    imagesBySlide () {
      const slides = this.slideDeck.slides
      const bgImgOpts = {
        w: 1900,
        h: 1400,
        fit: 'scale-down'
      }

      const imgOpts = {
        w: 1600,
        h: 1600,
        fit: 'scale-down'
      }

      const imagesBySlide = []

      if (!slides) { return imagesBySlide }

      slides.forEach((slide, index) => {
        const images = []

        if (slide.content.image && slide.content.image.length > 5) { images.push(this.cfResize(slide.content.image, imgOpts)) }

        if (slide.prompts[0] && slide.prompts[0].image && slide.prompts[0].image.length > 5) {
          images.push(this.cfResize(slide.prompts[0].image, imgOpts))
        }

        if (slide.prompts && slide.prompts.image && slide.prompts.image.length > 5) {
          images.push(this.cfResize(slide.prompts.image, imgOpts))
        }

        if (slide.options.bgImage && slide.options.bgImage.length > 5) { images.push(this.cfResize(slide.options.bgImage, bgImgOpts)) }
        if (slide.options.overlayImage && slide.options.bgImage.length > 5) { images.push(this.cfResize(slide.options.overlayImage, bgImgOpts)) }

        imagesBySlide[index] = images
      })

      return imagesBySlide
    },
    metaData () {
      return this.slideDeck.metaData || {}
    },
    isTest () {
      return this.$route.query.test === 'jmeter'
    },
    isSpectatorEvent () {
      return this.event?.settings?.isSpectatorEvent
    },
    bodyClass () {
      let classNames = ''
      if (!this.isModerator && this.activeEvent.player.name) {
        classNames += ' is-player'
      }

      if (this.isSpectatorEvent) {
        classNames += ' is-spectator'
      }

      return classNames
    },
    bodyStyle () {
      return {}
    },
    showPlayerControls () {
      return !this.isModerator && this.activeEvent.player.name
    },
    settings () {
      return this.event.settings || {}
    },
    activePlayer () {
      return this.activeEvent.player || {}
    }
  },
  watch: {
    // eslint-disable-next-line object-shorthand
    currentSlideIndex: function (newSlideIndex) {
      this.startPrefetchImages(newSlideIndex)
    }
  },
  mounted () {
    // graphql subscription is started from EventSubscription mixin
    // that will update this.event with all live data

    this.startPrefetchImages(0)
  },
  methods: {
    goToMainEvent () {
      this.$router.replace('/event/' + this.$route.params.id)
    },
    startPrefetchImages (slideIndex) {
      this.log('preparing to prefetch images after a random wait')
      const wait = Math.random() * (25000 - 2000) + 2000

      if (timeout) {
        this.log('prefetch timeout found, clearing and resetting')
        clearTimeout(timeout)
      }

      timeout = setTimeout(() => {
        this.prefetchNextImages(slideIndex + 2)
      }, wait)
    },
    prefetchNextImages (slide) {
      this.log('prefetching images to slide ' + slide)
      this.$nextTick(() => { this.prefetchImagesToSlide = slide })
    },
    setSlideDeck (slideDeck) {
      this.log('setting slideDeck: ', slideDeck)
      this.slideDeck = slideDeck
      if (!this.eventLoaded) { this.eventLoaded = true }
    },
    setEventId (data) {
      this.eventId = data.eventId
      this.primaryUser = data.primaryUser
    },
    setEventMeta (eventMeta) {
      this.log('setting eventMeta')
      if (!this.slideshowMounted) {
        this.$nextTick(() => {
          this.mountSlideshow()
          // this is initial load so set position for moderator also
          this.currentSlideIndex = eventMeta.position.currentSlide || 1
          this.step = eventMeta.position.step || 1
        })
      }

      // update position for players
      if (!this.isModerator) {
        this.currentSlideIndex = eventMeta.position.currentSlide || 1
        this.step = eventMeta.position.step || 1
      }

      this.eventMeta = eventMeta

      // if this is a demo, trigger re-render with demo responses
      if (eventMeta.settings.isPreviewEvent) {
        this.setResponses(this.realResponses)
      }

      // if there are removed players remove their local data as well
      if (eventMeta.settings.removedPlayers) {
        this.checkForRemovedPlayers(eventMeta.settings.removedPlayers)
      }

      this.$emit('event:update', this.event)
    },
    setPlayers (players) {
      // this.log('setting players: ', players)
      this.participants = Object.freeze(players)
      this.$emit('event:update', this.event)

      if (this.isModerator) {
        this.log('[POSTHOG] New players added for moderator, tracking data')
        this.posthogCapture('event:players_joined', {
          event_id: this.event.id,
          shortcode: this.event.shortcode,
          deck_id: this.slideDeck.id,
          deck_name: this.slideDeck.name,
          num_players: players.length
        })
      }
    },
    setResponses (responses) {
      if (!responses || responses.length < 1) { responses = [] }

      this.log('setting responses')
      this.responses = Object.freeze(responses)

      // add demo responses here
      if (this.event.settings.isPreviewEvent && responses.length > 0) {
        this.addDemoResponses()
      }

      this.$emit('event:update', this.event)
    },
    checkForRemovedPlayers (removedPlayers) {
      this.log('removed players found in event settings')

      const currentPlayerId = this.activeEvent.player?.id
      const newPlayersToRemove = removedPlayers.filter(id => !this.removedPlayers[id])
      if (newPlayersToRemove.length < 1) { return }

      // add newly removed players to local tracking object
      newPlayersToRemove.forEach((playerId) => {
        this.removedPlayers[playerId] = true
      })

      // clear local data of all removed players and removed responses
      this.clearRemovedPlayerData()

      if (!this.isModerator && currentPlayerId && this.removedPlayers[currentPlayerId] === true) {
        this.log('current player was removed, clear activeEvent.player and redirect')
        this.$store.commit('setActiveEvent', { id: '', shortcode: '', player: {}, lastActive: null })
        this.$buefy.toast.open('You have been removed from this event')
        this.$router.push('/go')
      }
    },
    removePlayer (playerId) {
      // add removed player and save event settings with new removedPlayers array
      const removedPlayers = this.eventMeta.settings.removedPlayers || []
      removedPlayers.push(playerId)
      this.saveEventSetting('removedPlayers', removedPlayers)
    },
    clearRemovedPlayerData () {
      this.log('new removed player found, removing from participants in SlideshowComponent _id.vue')

      // clear removedPlayers from participants
      this.participants = Object.freeze(this.participants.filter(p => !this.removedPlayers[p.id]))

      // clearing responses where id is removed
      // this.responses = Object.freeze(this.responses.filter(r => !this.removedPlayers[r.participantId]))
    },
    addDemoResponses () {
      this.log('adding demo responses at position: ', this.eventMeta.position)

      const slidePosition = this.event.position.currentSlide
      const currentSlide = this.slideDeck.slides[slidePosition - 1] || {}
      const responsesThisSlide = this.responses.filter(r => r.slidePosition === slidePosition)

      if (responsesThisSlide.length < 1) {
        this.log('user has not yet responded, waiting to add do responses')
        return // don't add demo responses until after user response
      }

      if (currentSlide.template === 'ratingSlide') {
        this.addDemoRatings()
      } else if (currentSlide.template === 'wordCloud') {
        this.addDemoWordCloud()
      }
    },
    addDemoRatings () {
      this.log('adding demo ratings')
      const players = this.participants
      const currentSlide = this.event.position.currentSlide

      if (this.demoResponses.find(resp => resp.slidePosition === currentSlide)) {
        this.log('already have ratings this slide, not adding more')
        return // already has demo responses this slide, don't add more
      }

      players.forEach((player, index) => {
        if (player.id > -1) { return } // skip real players with a positive integer id

        const num = index % 3 === 0 ? 4 : 5
        const response = {
          id: (player.id - 100000 - index) * Number(this.event.position.currentSlide),
          participantId: player.id,
          slidePosition: this.event.position.currentSlide,
          response: {
            number: num
          }
        }

        this.demoResponses.push(response)
      })
    },
    addDemoWordCloud () {
      const players = this.participants
      const currentSlide = this.event.position.currentSlide

      if (!this.demoResponses.find(resp => resp.slidePosition === currentSlide)) {
        this.log('no responses found, adding demo word cloud words')
        // doesn't have wordcloud responses yet, let's add them:
        players.forEach((player, index) => {
          if (player.id > -1) { return } // skip real players with a positive integer id
          const text = animals[index]
          const response = {
            id: (player.id - 100000 - index) * Number(currentSlide),
            participantId: player.id,
            slidePosition: currentSlide,
            response: {
              text
            }
          }
          this.demoResponses.push(response)
        })
      } else {
        this.log('already have word words this slide, not adding more')
      }

      if (this.event.position.step === 2) {
        this.log('step two, checking if we should add demo votes')
        this.addWordCloudVotes()
      }
    },
    addWordCloudVotes () {
      if (this.wordCloudVotes.length > 0) {
        this.log('previous votes found, not adding more')
        return
      }

      const slidePosition = this.event.position.currentSlide
      const responses = this.responses.filter(r => r.slidePosition === slidePosition)

      this.log('votes not found, adding them for the following responses')

      const first = responses.slice(0, 3)
      const second = responses.slice(0, 4)
      const third = responses.slice(0, 1)
      const fourth = responses.slice(6, 7)

      third.forEach((resp) => {
        resp.id = resp.id - 1000
        this.wordCloudVotes.push(resp)
      })

      setTimeout(() => {
        first.forEach((resp) => {
          resp.id = resp.id - 1000
          this.wordCloudVotes.push(resp)
        })
      }, 500)

      setTimeout(() => {
        second.forEach((resp) => {
          resp.id = resp.id - 1000
          this.wordCloudVotes.push(resp)
        })
      }, 1500)

      setTimeout(() => {
        third.forEach((resp) => {
          resp.id = resp.id - 1000
          this.wordCloudVotes.push(resp)
        })
      }, 2500)

      setTimeout(() => {
        fourth.forEach((resp) => {
          resp.id = resp.id - 1000
          this.wordCloudVotes.push(resp)
        })
      }, 3500)
    },
    toggleMenu (menu) {
      if (this.openMenu === menu) {
        this.openMenu = false
      } else {
        this.openMenu = menu
      }
    },
    saveEventSetting (setting, value) {
      const settings = Object.assign({}, this.event.settings)
      settings[setting] = value
      this.event.settings[setting] = value

      this.log('saving event settings: ', settings)

      this.$apollo.mutate({
        mutation: saveEventSettings,
        variables: {
          eventId: this.event.id,
          settings
        }
      })
    }
  }
}
</script>
<style lang="scss">
.event-page {
  background-color: #000;

  &.is-spectator {
    color: #eee;
  }
}
</style>
<style lang="scss" scoped>

.section.event {
  opacity: 1;
  transition: opacity .3s ease;

  &.event-not-loaded {
    opacity: 0;
  }
}

$padding: .7em;

.is-spectator {
  &.is-player .section.event {
    top: $padding;
    height: calc(100% - ($padding * 2));

    .spectator-banner {
      top: .3em;

      h2 {
        opacity: 1;
        font-size: 1.1em;
      }
      .join-code {
        opacity: .6;
        margin-top: .1em;
      }
    }
  }
  .section.event {
    width: calc(100% - ($padding * 2));
    height: calc(100% - ($padding * 5));
    position: absolute;
    top: $padding * 4;
    left: $padding;

    .hero.is-slide {
      border-radius: 8px;
      overflow: hidden;
    }

    :deep(.join-link) {
      position: fixed;
      top: 5px;
      right: 0;
      background-color: transparent;
      margin-left: auto;
      color: #eee;
      display: none;
    }

    .join-code {
      color: #eee;
      font-size: .8em;
      //position: absolute;
      //top: .1em;
      font-weight: normal;
      //right: .3em;
      display: flex;
      opacity: 1;
      align-items: center;

      .code {
        text-transform: uppercase;
        font-weight: bold;
        font-size: 1.2em;
      }
    }

    .return-to-main {
      font-size: .8em;
      font-weight: normal;
      opacity: .6;

      &:hover {
        cursor: pointer;
        text-decoration: underline;
        opacity: .8;

        i {
          position: relative;
          left: -2px;
        }
      }
    }

    .spectator-banner {
      position: fixed;
      top: .4em;
      left: .75em;
      width: calc(100% - ($padding * 2));
      font-size: 1.2em;
      font-weight: bold;
      letter-spacing: 1px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      h2 {
        opacity: .6;
      }
    }
  }
}

.prefetch-images {
  display: none;
}
.welcome {
  position: fixed;
  top: 0px;
  left: 0; right: 0;
  margin: 0 auto;
  text-align: center;
  width: 100%;
  background-image: linear-gradient(-39.6deg,#fdc78d 5.3%,#f98ffd 100.2%);
  background-image: linear-gradient( 106.4deg,  rgba(255,104,192,1) 11.1%, rgba(104,84,249,1) 81.3% );
  color: white;
  font-weight: bold;
  font-size: .9em;
  z-index: 10;
  height: 35px;
  line-height: 35px;
  box-shadow: 1px 2px 5px rgba(0,0,0,.15);
  text-shadow: 1px 2px 15px rgba(0,0,0,.2);

  span {
    text-transform: uppercase;
  }

  &.host-warning {
    background-image: linear-gradient( 106.4deg,  rgb(255, 104, 117) 11.1%, rgb(230, 157, 47) 81.3% );
  }
}

@media screen and (min-width: 768px) {
  .welcome.host-warning {
    display:none;
  }
}

.hero.is-slide.isPlayer{
  top: 35px;
}

.reaction-fab {
  position: fixed;
  background:linear-gradient(195deg, #fff 60%, #ddd);
  border-radius: 50%;
  // border-top-left-radius: 0;
  // border-bottom-left-radius: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  box-shadow: 1px 4px 12px -2px rgba(0,0,0,.3);
  // letter-spacing: -5px;
  line-height: 1.5em;
  font-size: 1.5em;
  text-align: center;
  bottom:2rem;
  right: 1.8rem;
  width: 54px;
  height: 54px;
  z-index: 1;
}

.player-menu {
  width: auto;
  position: fixed;
  bottom: 2rem;
  height: 54px;
  left: 1.8rem;
  // right: 86px;
  border-radius: 10px;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  background: linear-gradient(185deg, #fff 60%, #e6e6e6);
  box-shadow: 1px 4px 12px -2px rgba(0,0,0,.2);

  .logo {
    width: 5em;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    span {
      font-size: 1.5em;
    }

    svg {
      width: 3.75em;
      height: auto;
    }
  }
}
</style>
